import { Box, Button, Card, CardContent, Checkbox, Container, FormControlLabel, makeStyles, Step, StepContent, StepLabel, Stepper } from "@material-ui/core";
import { Check } from "@material-ui/icons";
import { useContext, useEffect, useRef, useState } from "react";
import { BulletinSearchResItem, BulletinSearchResItemBulletin, useBulletinApi } from "./js/adapter/BulletinAdapter";
import { useHistory } from "react-router-dom";
import { MainPageDialogContext } from "./js/MainPageDialogContext";
import { ResultObj } from "./js/Domain";
import { CircleProgress } from "./components/Overlay";

// 步驟節點樣式
const useQontoStepIconStyles = makeStyles(() => ({
    root: {
        color: '#eaeaf0',
        display: 'flex',
        height: 22,
        alignItems: 'center',
    },
    active: {
        color: '#3f51b5',
    },
    circle: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: 'currentColor',
    },
    completed: {
        color: '#3f51b5',
        zIndex: 1,
        fontSize: 18,
    },
}));

// 步驟條狀態判斷
const QontoStepIcon = (props: { active: boolean, completed: boolean }) => {
    const classes = useQontoStepIconStyles();
    const { active, completed } = props;

    return (
        <Box
            className={`${classes.root} ${active ? classes.active : ''}`}>
            {completed ? <Check className={classes.completed} /> : <Box className={classes.circle} />}
        </Box>
    );
}

interface ReactStepperHorizontalProps {
    checkBoxLock: boolean;
    activeStep: number;
    steps: { title: string }[];
    content: React.ReactNode;
    nextTabs: (step: number) => void;
    backTabs: (step: number) => void;
    confirmBtn?: () => void;
}

// 步驟條
const ReactStepperHorizontal = (props: ReactStepperHorizontalProps) => {

    const {
        activeStep,
        content,
        steps,
        checkBoxLock: propsCheckBoxLock,
        confirmBtn,
        nextTabs,
        backTabs
    } = props;

    const [checked, setChecked] = useState<boolean>(true);

    // 下一步驟按鈕
    const nextBtn = () => {
        nextTabs(1);
        setChecked(true);
    }

    // 上一步驟按鈕
    const backBtn = () => {
        backTabs(1);
        setChecked(true);
    }

    // checkBox 勾選後解除按鈕
    const handleChange = () => {
        setChecked(!checked);
    }

    return (<Stepper activeStep={activeStep} orientation="vertical" style={{ padding: "0" }}>
        {steps.map((item, index) => (
            <Step key={`step-${index}-${item.title}`} >
                <StepLabel StepIconComponent={QontoStepIcon}>
                    {item.title}
                </StepLabel>
                <StepContent>
                    <Box p="5px 0px">
                        {content}
                    </Box>
                    <Box display="flex" justifyContent="flex-end" alignItems="center" margin="1rem 1rem 0 1rem">
                        <FormControlLabel
                            disabled={propsCheckBoxLock}
                            control={<Checkbox color="primary" onChange={handleChange} />}
                            label="本人已閱讀及同意遵守上述條款及細則" />
                        {activeStep !== 0 &&
                            <Button
                                onClick={backBtn}>
                                返回
                            </Button>
                        }
                        <Button
                            style={{ whiteSpace: "nowrap" }}
                            variant="contained"
                            color="primary"
                            disabled={checked}
                            onClick={activeStep === steps.length - 1 ? (confirmBtn ?? nextBtn) : nextBtn}>
                            {activeStep === steps.length - 1 ? '確認' : '下一步'}
                        </Button>
                    </Box>
                </StepContent>
            </Step>
        ))}
    </Stepper>)
}

// 守則卡片
const RulesCard = () => {
    const bulletinApiManager = useBulletinApi();
    const history = useHistory()

    // 當前步驟
    const [activeStep, setActiveStep] = useState(0);
    // 勾選鎖定
    const [checkBoxLock, setCheckBoxLock] = useState(true);
    // API是否讀取完成
    const [isLoaded, setIsLoaded] = useState(false);
    // 公告內容
    const [bulletinArr, setBulletinArr] = useState<BulletinSearchResItemBulletin[]>([]);

    const contentRef = useRef<HTMLDivElement>(null);

    // 卷軸滑到最底解除按鈕鎖定
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollHeight, scrollTop, clientHeight } = e.currentTarget;
        const scroll = scrollHeight - scrollTop - clientHeight
        if (scroll <= 2) {
            setCheckBoxLock(false);
        }
    }

    // 下一步
    const fcnNext = (num: number) => {
        setActiveStep(activeStep + num);
        setCheckBoxLock(true);
    }

    // 上一步
    const fcnBack = (num: number) => {
        setActiveStep(activeStep - num);
        setCheckBoxLock(true);
    }
    // 確認並已讀後跳頁
    const confirmBtn = async () => {
        const bulletinIds = bulletinArr.map((item) => item.bulletin_id)

        try {
            await bulletinApiManager.BulletinRead({
                bulletin_ids: bulletinIds,
            })
            history.push('/Index/Home');
        } catch (error) {
            console.error(error);
        }
    }

    // 一進畫面先查全部必須同意且尚未已讀的公告
    useEffect(() => {
        const getBulletinData = async () => {
            try {
                const { data } = await bulletinApiManager.BulletinSearch({
                    is_login_read: "Y",
                    is_read: "N"
                })

                if (data.bulletins.length === 0) {
                    //如果沒有需要顯示的公告就導回首頁
                    history.push('/Index/Home');
                } else {
                    setBulletinArr(data.bulletins);
                    setIsLoaded(true);
                }
            } catch (error) {
                console.error(error);
            }
        }
        if (!isLoaded) {
            getBulletinData()
        }
    }, [isLoaded, history, bulletinApiManager]);

    if (!isLoaded) return <CircleProgress />

    const CH = contentRef.current?.clientHeight || 0

    return (<Card style={{ maxHeight: "80%", overflow: "auto" }}>
        <CardContent>
            <Box id="scrollLock" style={{ overflow: "auto" }}>
                <ReactStepperHorizontal
                    checkBoxLock={CH >= 300 && checkBoxLock}
                    activeStep={activeStep}
                    steps={bulletinArr.map(item => ({ title: item.subject }))}
                    content={
                        <div
                            ref={contentRef}
                            style={{ maxHeight: "300px", maxWidth: "600px", overflow: "auto", paddingLeft: "20px" }}
                            dangerouslySetInnerHTML={{ __html: bulletinArr[activeStep].content }}
                            onScroll={handleScroll}
                        >
                        </div>
                    }
                    nextTabs={fcnNext}
                    backTabs={fcnBack}
                    confirmBtn={confirmBtn}
                />
            </Box>
        </CardContent>
    </Card>)
}


// 最外層
const EmployeeRules = () => {
    return (
        <Container style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <RulesCard />
        </Container>
    )
}

export default EmployeeRules;